<script lang="ts" setup generic="TableData extends Api.Common.CommonRecord">
import type { Ref, VNode, WatchStopHandle } from 'vue';
import { computed, h, onMounted, ref, toValue, watch } from 'vue';
import type { ModalReactive, DataTableColumns as NTableColumns } from 'naive-ui';
import { NButton, NDataTable, NPopconfirm, NSwitch, NTooltip, createDiscreteApi } from 'naive-ui';
import { useSvgIconRender } from '@sa/hooks';
import type { SortableEvent, UseDraggableReturn } from 'vue-draggable-plus';
import { useDraggable } from 'vue-draggable-plus';
import { useAppStore } from '@/store/modules/app';
import { useAuth } from '@/hooks/business/auth';
import useDataLoading from '@/hooks/common/use-data-loading';
import type { ApiFn, BasicColumn, TableColumn } from '@/components/advanced/basic-table/use-table';
import { useTable } from '@/components/advanced/basic-table/use-table';
import { $t } from '@/locales';
import type { Api } from '@/typings/api';
import svgIcon from '@/components/custom/svg-icon.vue';
import type { BasicForm } from '@/typings/basic-form';
import type { BasicTableExpose, BasicTableProps } from '@/typings/basic-table';
import modalUpload from '@/components/modal/modal-upload/modal-upload.vue';
import { nanoid } from '~/packages/utils/src';

const { SvgIconVNode } = useSvgIconRender(svgIcon);

const { hasAuth } = useAuth();

const { modal } = createDiscreteApi(['modal']);

const { isLoading, setLoading } = useDataLoading();

/** 自定义表格组件 */
defineOptions({
  name: 'BasicTable'
});

const props = withDefaults(defineProps<BasicTableProps<TableData>>(), {
  isCard: true,
  rowKey: 'id',
  isPage: true,
  isTree: false,
  showSearch: true,
  childrenKey: 'children',
  basicColumns: () => [],
  showTableOperation: true,
  tableOperation: () => [],
  rowOperation: () => [],
  showRowOperation: undefined,
  isFilterColumns: true,
  fetchRequiredColumns: () => ['id'],
  customOperationRender: undefined,
  defaultExpandAll: false,
  dragable: false,
  dragHandler: '.n-data-table-tr td:first-child',
  dragColumnKey: undefined
});

const emit = defineEmits<{
  'update:expandedRowKeys': [expandedRowKeys: Array<string | number>];
  delete: [id: string];
  // edit?: [id: string];
  deleteBatch: [ids: string[]];
  mounted: [
    {
      data: Ref<TableData[]>;
    }
  ];
  /** 导入成功 */
  importSuccess: [
    {
      tableData: Ref<TableData[]>;
      importData: any[];
    }
  ];
  /** 导入失败 */
  importFail: any;
}>();

const appStore = useAppStore();
/** 表格ID */
const tableId = `basic-table-${nanoid()}`;

// 操作列
const operationColumn: Ref<TableColumn<TableData, BasicColumn> | null> = computed(() => {
  let rowOperation: TableColumn<TableData, BasicColumn> | null = null;
  if (props.rowOperation && props.rowOperation.length > 0) {
    rowOperation = {
      key: 'operate',
      title: $t('common.operate'),
      dataType: 'none',
      titleAlign: 'center',
      align: 'left',
      width: 16 + 46 * props.rowOperation.length,
      fixed: 'right',
      render: row => {
        const tags: VNode[] = [];
        let customOperationRender = null;
        if (props.customOperationRender && typeof props.customOperationRender === 'function') {
          customOperationRender = props.customOperationRender(row);
        }
        for (const key of props.rowOperation) {
          if (props.showRowOperation && !props.showRowOperation(key, row)) {
            // eslint-disable-next-line no-continue
            continue;
          }
          switch (key) {
            case 'view':
              if (hasPermission('view')) {
                tags.push(
                  h(NTooltip, null, {
                    trigger: () =>
                      h(NButton, {
                        type: 'primary',
                        size: 'small',
                        secondary: true,
                        renderIcon: SvgIconVNode({ icon: 'mdi:text-box-search' }),
                        onClick: () => handleView(row)
                      }),
                    default: () => '查看'
                  })
                );
              }
              break;
            case 'edit':
              if (hasPermission('update')) {
                tags.push(
                  h(NTooltip, null, {
                    trigger: () =>
                      h(NButton, {
                        type: 'warning',
                        size: 'small',
                        secondary: true,
                        renderIcon: SvgIconVNode({ icon: 'mdi:square-edit-outline' }),
                        onClick: () => handleEdit(row)
                      }),
                    default: () => '编辑'
                  })
                );
              }
              break;
            case 'delete':
              if (hasPermission('delete')) {
                tags.push(
                  h(NTooltip, null, {
                    trigger: () =>
                      h(
                        NPopconfirm,
                        {
                          onPositiveClick: () => handleDelete(row[props.rowKey] as string)
                        },
                        {
                          default: () => $t('common.confirmDelete'),
                          trigger: () =>
                            h(NButton, {
                              type: 'error',
                              size: 'small',
                              secondary: true,
                              renderIcon: SvgIconVNode({ icon: 'mdi:delete-outline' }),
                              loading: isLoading(row[props.rowKey] as string).value
                            })
                        }
                      ),
                    default: () => '删除'
                  })
                );
              }
              break;
            default:
              if (customOperationRender && key in customOperationRender && customOperationRender[key]) {
                tags.push(customOperationRender[key]!);
              }
          }
        }
        return h(
          'div',
          {
            class: 'p-l-8px p-r-8px flex-center gap-8px'
          },
          tags
        );
      }
    };
  }
  return rowOperation;
});

const finalTableOperation = computed(() => {
  const tableOperationVal = [];
  if (props.tableOperation && toValue(props.tableOperation).length > 0) {
    tableOperationVal.push(...toValue(props.tableOperation).filter(item => hasPermission(item)));
  }
  if (!props.fetchFn && tableOperationVal.includes('refresh')) {
    // 移除refresh
    tableOperationVal.splice(tableOperationVal.indexOf('refresh'), 1);
  }
  return tableOperationVal;
});

/** 排序 */
const orders = ref<Api.Common.Order[]>([]);
/** 过滤条件 */
const filters = ref<Api.Common.Filter[]>([]);

/** 表格列 */
const propColumns = computed(() => {
  return operationColumn.value === null ? props.columns : [...props.columns, operationColumn.value];
});
/** 可见列 */
const visibleColumns = ref<string[]>([]);

/** 是否静态数据 */
const isStaticTable = computed(() => {
  return (props.data !== null && props.data !== undefined) || !props.fetchFn;
});

const {
  dataTableRef,
  columns,
  filteredColumns,
  searchFields,
  data,
  enableCrossPageChecked,
  checkedRowKeys,
  totalNumber,
  loading,
  pagination,
  updateSearchParams,
  getData,
  exportData,
  startLoading,
  endLoading
} = useTable<TableData, ApiFn, BasicColumn>({
  apiFn: props.fetchFn,
  apiParams: {
    column: visibleColumns,
    filter: filters,
    order: orders
  },
  data: computed(() => props.data),
  isStaticTable,
  isPage: computed(() => props.isPage),
  isTree: computed(() => props.isTree),
  parentKey: props.parentKey,
  childrenKey: props.childrenKey,
  selectionColumnConfig: props.selectionColumnConfig,
  basicColumns: props.basicColumns ? props.basicColumns : ['selection', 'index'],
  columns: propColumns,
  exportFn: props.exportFn,
  exportColumns: props.exportColumns,
  exportParams: props.exportParams,
  immediate: !props.isFilterColumns,
  mergeColumns: props.mergeColumns
});

/** 横向滚动条宽度 */
const scrollXRef = computed(() =>
  columns.value.reduce((total, row) => {
    // eslint-disable-next-line no-nested-ternary
    let width = row.width ? (typeof row.width === 'number' ? row.width : Number.parseInt(row.width, 10)) : 100;
    if ('children' in row && row.children.length > 0) {
      width = row.children.reduce((subTotal, child) => {
        // eslint-disable-next-line no-nested-ternary
        const subWidth = child.width
          ? typeof child.width === 'number'
            ? child.width
            : Number.parseInt(child.width, 10)
          : 100;
        return subTotal + subWidth;
      }, 0);
    }
    return total + width;
  }, 0)
);

function handleSorterChange(sorter: any) {
  if (sorter.order === false) {
    orders.value.length = 0;
  } else {
    orders.value = [
      {
        column: sorter.columnKey,
        sort: sorter.order === 'ascend'
      }
    ];
  }
  getData();
}

const operationToAsset: Record<string, string> = {
  add: 'save',
  edit: 'update'
};
/**
 * 权限判断
 *
 * @param key
 */
function hasPermission(key: string) {
  if (props.authModule) {
    return hasAuth(`${props.authModule}:${key in operationToAsset ? operationToAsset[key] : key}`);
  }
  return true;
}

watch(
  () => {
    return {
      a: props.isFilterColumns,
      b: props.columns,
      filteredColumns
    };
  },
  () => {
    if (props.isFilterColumns) {
      const originalColumns = props.columns.flatMap(item => {
        if ('key' in item) {
          if ('tempColumn' in item && item.tempColumn) {
            return [];
          }
          return [item.key as string];
        }
        return [];
      });
      const columnReuiredColumns: string[] = [];
      const showColumns = filteredColumns.value.flatMap(item => {
        const temp = [item.key];
        if (item.requestRequiredColumns) {
          columnReuiredColumns.push(...item.requestRequiredColumns);
        }
        return temp;
      });
      visibleColumns.value = originalColumns
        .filter(item => showColumns.includes(item))
        .concat(columnReuiredColumns)
        .concat(props.fetchRequiredColumns);
      if (!props.showSearch) {
        getData();
      }
    }
  },
  {
    immediate: true
  }
);

// watchEffect(() => {
//   if (props.isFilterColumns) {
//     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
//     // @ts-expect-error
//     const originalColumns = props.columns.map(item => item.key);
//     const showColumns = filteredColumns.value.map(item => item.key);
//     visibleColumns.value = originalColumns
//       .filter(item => showColumns.includes(item))
//       .concat(props.fetchRequiredColumns);
//     getData();
//   }
// });

// 静态数据监听
let unwatchFn: WatchStopHandle | null = null;
watch(
  isStaticTable,
  () => {
    if (isStaticTable.value) {
      unwatchFn = watch(
        () => props.data,
        () => {
          getData();
        }
      );
    } else if (unwatchFn) {
      unwatchFn();
    }
  },
  {
    immediate: true
  }
);

/** 拖拽排序 */
const dragableRef: Ref<UseDraggableReturn | null> = ref(null);
watch(
  () => [props.dragable, data.value.length],
  () => {
    if (props.dragable) {
      if (data.value.length > 0 && dragableRef.value === null) {
        dragableRef.value = useDraggable(`#${tableId} .n-data-table-tbody`, data, {
          handle: props.dragHandler,
          onEnd: (_event: SortableEvent) => {
            if (props.dragColumnKey) {
              data.value.forEach((item, index) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                item[props.dragColumnKey] = index + 1;
              });
            }
          }
        });
      } else if (data.value.length === 0 && dragableRef.value !== null) {
        dragableRef.value.destroy();
        dragableRef.value = null;
      }
    } else {
      dragableRef.value?.destroy();
      dragableRef.value = null;
    }
  },
  {
    immediate: true
  }
);

/** 操作类型 */
const operation: Ref<BasicForm.OperationType> = ref('view');
/** 当前选择行数据 */
const currentRow: Ref<TableData | null> = ref(null);
/** 侧边栏是否可见 */
const modalVisible: Ref<boolean> = ref(false);

/** 渲染表格操作 */
function renderTableOperation() {
  return props.tableOperationRender ? props.tableOperationRender({ tableData: data }) : null;
}

/** 查看数据 */
function handleView(row: TableData) {
  currentRow.value = row;
  operation.value = 'view';
  modalVisible.value = true;
}

/** 添加数据 */
function handleAdd() {
  currentRow.value = null;
  operation.value = 'add';
  modalVisible.value = true;
}

/** 编辑数据 start */
function handleEdit(row: TableData) {
  currentRow.value = row;
  operation.value = 'edit';
  modalVisible.value = true;
}
/** 编辑数据 end */

/** 删除数据 start */
/** 批量删除 */
async function handleBatchDelete() {
  if (checkedRowKeys.value.length === 0 || !props.batchDeleteFn) {
    return;
  }
  startLoading();
  const checkedKeys = checkedRowKeys.value;
  const result = await props.batchDeleteFn(checkedKeys);
  endLoading();
  if (!result?.error) {
    checkedRowKeys.value.length = 0;
    emit('deleteBatch', checkedKeys);
    window.$message?.success('删除成功');
    getData();
  }
}

/** 删除数据 */
async function handleDelete(id: string) {
  setLoading(id, true);
  try {
    const result = await props.deleteFn?.(id);
    if (!result?.error) {
      emit('delete', id);
      window.$message?.success('删除成功');
    }
  } finally {
    setLoading(id, false);
  }
  getData();
}
/** 删除数据 end */

function handleSearch(params: Api.Common.Filter[]) {
  filters.value = params;
  if (pagination !== false) {
    updateSearchParams({ pageable: { page: 0, size: pagination?.pageSize } });
  }
  getData();
}

/** 刷新数据，重置分页 */
function reload() {
  if (pagination !== false) {
    updateSearchParams({ pageable: { page: 0, size: pagination?.pageSize } });
  }
  getData();
}

/** 展开行key变化 */
function handleExpandedRowKeys(expandedRowKeys: Array<string | number>) {
  emit('update:expandedRowKeys', expandedRowKeys);
}

/** 导入数据 */
const canCloseImportModal = ref(true);
function createModalUploadEl(m: ModalReactive) {
  return () =>
    h(modalUpload, {
      templateName: props.importTemplateName?.toString() || '',
      importFn: props.importFn,
      data: props.importExtData,
      title: props.importTitle,
      onStartUpload: () => {
        canCloseImportModal.value = false;
      },
      onSuccess: res => {
        emit('importSuccess', {
          tableData: data,
          importData: res
        });
        m.destroy();
        window.$message?.success('导入成功');
        if (!isStaticTable.value) {
          getData();
        }
      },
      onFail: e => emit('importFail', e),
      onFinally: () => {
        canCloseImportModal.value = true;
      }
    });
}

function handleImport() {
  // 上传组件
  const m = modal.create({
    title: '导入数据',
    preset: 'card',
    style: {
      width: '500px'
    },
    maskClosable: false,
    closeOnEsc: false,
    closable: true,
    onClose: () => closeImportModal(m)
  });
  m.content = createModalUploadEl(m);
  return m;
}

function closeImportModal(m: ModalReactive) {
  if (canCloseImportModal.value) {
    m.destroy();
  }
}
/** 导入数据 end */

/** 导出数据 */
async function handleExport() {
  setLoading('export', true);
  return exportData()
    .then(_res => {
      // console.log('🚀 ~ .then ~ res:', res);
      // window.$message?.success('导出成功');
    })
    .catch(() => {
      window.$message?.error('导出失败');
    })
    .finally(() => {
      setLoading('export', false);
    });
}
/** 导出数据 end */

/** 暴露给父组件 */
defineExpose<BasicTableExpose<TableData>>({
  data,
  dataTableRef,
  operation,
  currentRow,
  modalVisible,
  selectedRowKeys: checkedRowKeys,
  totalNumber,
  getData,
  reload,
  loading,
  startLoading,
  endLoading
});
onMounted(() => {
  emit('mounted', {
    data
  });
});
</script>

<template>
  <TableSearch
    v-if="showSearch"
    :search-fields="searchFields"
    :label-width="props.labelWidth"
    @search="handleSearch"
  ></TableSearch>
  <NCard
    :title="
      () =>
        h('div', null, [
          title + ` (${totalNumber})`,
          isPage && checkedRowKeys.length > 0
            ? [
                h(
                  'span',
                  {
                    class: 'ml-24px mr-10px font-size-14px'
                  },
                  '跨页选择'
                ),
                h(NSwitch, {
                  size: 'small',
                  value: enableCrossPageChecked,
                  onUpdateValue: (val: any) => (enableCrossPageChecked = Boolean(val))
                })
              ]
            : null
        ])
    "
    :bordered="false"
    size="small"
    class="sm:flex-1-hidden"
    :class="isCard ? 'card-wrapper' : ''"
    :header-class="isCard ? '' : '!pl-0 !pr-0'"
    :content-class="isCard ? '' : '!p-0'"
  >
    <template #header-extra>
      <TableHeaderOperation
        v-if="showTableOperation"
        v-model:columns="filteredColumns"
        :table-operation="finalTableOperation"
        :disabled-delete="checkedRowKeys.length === 0"
        :loading="loading"
        :button-loading="isLoading"
        @add="handleAdd"
        @delete="handleBatchDelete"
        @refresh="getData"
        @import="handleImport"
        @export="handleExport"
      >
        <template #default>
          <slot name="header-extra">
            <component :is="renderTableOperation" v-if="tableOperationRender" />
          </slot>
        </template>
      </TableHeaderOperation>
    </template>
    <NDataTable
      :id="tableId"
      ref="dataTableRef"
      v-model:checked-row-keys="checkedRowKeys"
      :data="data"
      size="small"
      :single-line="false"
      :flex-height="!appStore.isMobile"
      :scroll-x="scrollXRef"
      :loading="loading"
      remote
      :pagination="isPage ? pagination : false"
      :row-key="item => item[rowKey]"
      pagination-behavior-on-filter="first"
      class="sm:h-full"
      :class="{
        'min-h-[500px]': !tableStyle?.height,
        'drag-index': dragable && dragHandler === '.n-data-table-tr td:first-child'
      }"
      :style="tableStyle"
      :default-expand-all="defaultExpandAll"
      :expanded-row-keys="expandedRowKeys"
      :columns="columns as NTableColumns<any>"
      @update:expanded-row-keys="handleExpandedRowKeys"
      @update:sorter="handleSorterChange"
      @update:checked-row-keys="onChecked"
    />
  </NCard>
</template>

<style scoped>
:deep(.drag-index .n-data-table-tr td:first-child) {
  cursor: move;
}
</style>

<style>
.n-data-table-base-table-header {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.n-data-table-base-table-header::-webkit-scrollbar {
  display: none;
}
.n-data-table-td {
  --n-merged-border-color: #888888;
}
</style>
